import { useIsMutating } from '@tanstack/react-query'
import { ArrowRightIcon } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import {
  BorderedRadioButton,
  Button,
  NumberMask,
  RadioGroup,
  Select,
  TextInput,
  usePageTitle
} from 'slab'
import { usePostQuotingProspectsProspectIdQuotesIdNotifySlackAboutLargeSelfServeOpportunity as useNotifySlack, usePutEmployersEmployersIdProfile as useUpdateEmployerProfile, usePutQuotingProspectsId } from 'utils/web_api'
import { EmployersEmployerIndustry as EmployerIndustry, type PutEmployersEmployersIdProfileIndustry } from 'utils/web_api.schemas'

import { CenteredLayout } from '../CenteredLayout'
import { useEmployer } from '../EmployerProvider'
import { usePrefill } from '../PrefillProvider'
import { useQuote } from '../QuoteProvider'
import { useAPIErrorBanner } from '../useErrorBanner'
import { emitEmployerOnboardingEvent, parseNumber } from '../utils'
import { useProspect } from './ProspectProvider'

const INDUSTRY_OPTIONS = Object.values(EmployerIndustry)
  .filter(industry => industry !== 'other')
  .map(industry => ({
    label: industry
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase())
      .replace(/\bAnd\b/g, 'and') // don't capitalize "and"
      .replace(/\bOf\b/g, 'of'), // don't capitalize "of"
    value: industry,
  }))

export function AdditionalInfoPage () {
  const { employer, refetch: refetchEmployer } = useEmployer()
  const { prospect, refetch: refetchProspect } = useProspect()
  const { quote } = useQuote()
  const isMutating = useIsMutating()
  const { navigateToPreviousStep, navigateToNextStep } = useOnboardingStep()
  const { getPrefillValue } = usePrefill()

  usePageTitle(`Tell us more about ${employer?.name}`)

  const { errorBanner, setError } = useAPIErrorBanner({ fallbackMessage: 'Could not update your info because of an error' })

  const form = useForm<{
    employees_in_multiple_states: 'yes' | 'no'
    industry: string | null
    employee_count: number | null
    estimated_employee_count_in_one_year: number | null
  }>({
    defaultValues: {
      employees_in_multiple_states: prospect?.employees_in_multiple_states ? 'yes' : 'no',
      industry: employer?.industry ?? getPrefillValue('industry'),
      employee_count: prospect?.employee_count ?? getPrefillValue('employee_count'),
      estimated_employee_count_in_one_year: prospect?.estimated_employee_count_in_one_year ?? getPrefillValue('estimated_employee_count_in_one_year'),
    },
    mode: 'onTouched',
  })

  // All of the prospect fields are non-nilable, so industry is the only way we can check
  // if we should set a default for has employees in multiple states
  useEffect(() => {
    if (!employer?.industry) {
      const correctValue = prospect?.census_employees_in_multiple_states ? 'yes' : 'no'
      form.setValue('employees_in_multiple_states', correctValue)
    }
  }, [employer, prospect, form])

  const updateProspectMutation = usePutQuotingProspectsId({
    mutation: {
      onError: err => setError(err)
    }
  })
  const updateEmployerMutation = useUpdateEmployerProfile({
    mutation: {
      onError: err => setError(err)
    }
  })

  const notifySlackMutation = useNotifySlack()

  const handleSubmit = async () => {
    if (!prospect?.id || !employer?.id) return

    const data = form.getValues()
    const [prospectResult, employerResult] = await Promise.allSettled([
      updateProspectMutation.mutateAsync({
        id: prospect.id,
        data: {
          employees_in_multiple_states: data.employees_in_multiple_states === 'yes',
          employee_count: data.employee_count,
          estimated_employee_count_in_one_year: data.estimated_employee_count_in_one_year,
        }
      }),
      updateEmployerMutation.mutateAsync({
        id: employer.id,
        data: {
          industry: data.industry as PutEmployersEmployersIdProfileIndustry,
          number_of_employees: data.employee_count ?? undefined,
        },
      }),
    ])

    if (prospectResult.status === 'fulfilled' && employerResult.status === 'fulfilled') {
      emitEmployerOnboardingEvent('[ER] Company additional details added', {
        company_size: data.employee_count,
        multi_state: data.employees_in_multiple_states,
        future_ees: data.estimated_employee_count_in_one_year,
      })

      if (quote?.id && data.employee_count && employer?.quoting_prospect_id) {
        notifySlackMutation.mutate({
          prospectId: employer.quoting_prospect_id,
          id: quote.id,
          data: {
            employee_count: data.employee_count,
          },
        })
      }

      await Promise.all([refetchProspect(), refetchEmployer()])

      navigateToNextStep()
    }
  }

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header
        heading={`Looking good! Now let’s understand a bit more about ${employer?.name}`}
        subheading='This helps us figure out the best fit for your team, based on what other companies like you are offering.'
      />
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <CenteredLayout.Container>
          <RadioGroup
            label='Do you have employees in more than 1 state?'
            layout='horizontal'
            gap={3}
            name='employees_in_multiple_states'
          >
            <BorderedRadioButton
              {...form.register('employees_in_multiple_states', {
                required: true,
              })}
              value='yes'
              label='Yes'
              size='small'
            />
            <BorderedRadioButton
              {...form.register('employees_in_multiple_states', {
                required: true,
              })}
              value='no'
              label='No'
              size='small'
            />
          </RadioGroup>
          <Select
            label={`Which industry does ${employer?.name} operate in?`}
            placeholder='Select an industry'
            required
            {...form.register('industry', {
              required: 'This field is required',
            })}
            error={form.formState.errors.industry?.message}
          >
            {INDUSTRY_OPTIONS.sort().map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
            <option value='other'>Other</option>
          </Select>
          <NumberMask decimalPlaces={0}>
            <TextInput
              label='How many full-time employees do you have?'
              {...form.register('employee_count', {
                required: 'This field is required',
                validate: value => {
                  if (value !== null && value < 1) {
                    return 'Must be greater than 0'
                  }

                  return true
                },
                setValueAs: parseNumber
              })}
              error={form.formState.errors.employee_count?.message}
            />
          </NumberMask>
          <NumberMask decimalPlaces={0}>
            <TextInput
              label="How many employees do you think you'll have in 1 year's time?"
              description='This is meant to be an estimate, no pressure to be exact.'
              {...form.register('estimated_employee_count_in_one_year', {
                required: 'This field is required',
                validate: value => {
                  if (value !== null && value < 1) {
                    return 'Must be greater than 0'
                  }

                  return true
                },
                setValueAs: parseNumber,
              })}
              error={form.formState.errors.estimated_employee_count_in_one_year?.message}
            />
          </NumberMask>
          {errorBanner}
        </CenteredLayout.Container>
        <CenteredLayout.Buttons>
          <Button variant='secondary' onClick={navigateToPreviousStep}>
            Back
          </Button>
          <Button
            variant='primary'
            type='submit'
            rightIcon={ArrowRightIcon}
            disabled={!!isMutating}
          >
            Next step
          </Button>
        </CenteredLayout.Buttons>
      </form>
    </CenteredLayout>
  )
}
